// Zod DTOs for the Phase-0 auth surface (spec §6.1) — the single source of
// truth for request/response contracts consumed by `apps/api` controllers
// (via `ValidationPipe`/a zod adapter) and `apps/web` (VeeValidate + Zod,
// §9.1). Neither app should hand-roll a parallel shape for these routes;
// import from here.
import { z } from 'zod';
import { EnrollmentStatus, Role, UserStatus } from '../enums';
import { emailSchema, idSchema, nameSchema, opaqueTokenSchema, passwordSchema } from './primitives';

/** `z.enum` needs a non-empty string-literal tuple, not `string[]` — these
 * casts are safe because {@link Role.ADMIN} et al. always populate the
 * `const` object with at least one key. */
const roleEnumSchema = z.enum(Object.values(Role) as [Role, ...Role[]]);
const enrollmentStatusEnumSchema = z.enum(
  Object.values(EnrollmentStatus) as [EnrollmentStatus, ...EnrollmentStatus[]],
);

// ─── POST /v1/auth/login ───────────────────────────────────────────────────

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  // Refresh token itself is httpOnly-cookie-only (spec §6.1, §10) — never in
  // the JSON body — so the response only echoes the access token + user.
  user: z.lazy(() => authMeResponseSchema),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// ─── POST /v1/auth/refresh · /v1/auth/logout ───────────────────────────────
// Both are cookie-driven (rotating refresh token / clear cookie); no request
// body. Response for refresh mirrors login's access-token envelope.

export const refreshResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  // Optional so any client built before this field existed still validates —
  // folds the `/v1/auth/me` round trip into `/v1/auth/refresh` for boot-time
  // callers that want both in one request (round-trip collapse, see
  // apps/api/src/routes/auth.ts).
  user: z.lazy(() => authMeResponseSchema).optional(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

// ─── POST /v1/auth/register (self-signup, §15.8) ───────────────────────────
// Open registration creates a `STUDENT` in `PENDING_VERIFICATION` — never
// `ACTIVE` — until `verify-email` succeeds (`EMAIL_NOT_VERIFIED` gates login
// until then, see ../errors.ts).

export const registerRequestSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  // Optional signup preference (AUTH-01, `docs/design/
  // AUTH-01-registration-board-level-decision.md` §3) — purely additive so
  // every already-shipped client body stays valid. Validated (published +
  // not archived + board/level agreement) in the route handler, never
  // echoed back in `registerResponseSchema` (§3.4: keep the response
  // byte-identical between the fresh and already-registered-email cases).
  examBoardId: idSchema.optional(),
  levelId: idSchema.optional(),
  // Turnstile-captcha-on-register backend task (2026-08-27/29): the
  // Cloudflare Turnstile widget's response token, verified server-side
  // (`https://challenges.cloudflare.com/turnstile/v0/siteverify`) before
  // any account is created — this endpoint sends a real SES verification
  // email per signup. Field name is LOCKED — `apps/web`'s Turnstile widget
  // sends it under this exact name.
  //
  // Optional (2026-08-29 follow-up, PO ruling): no real production
  // sitekey/secret exists yet, so the whole feature is gated behind the
  // API's `TURNSTILE_ENABLED` flag, off by default. This shared DTO has no
  // access to that server-side flag, so it can't enforce requiredness
  // itself — the register route handler (`apps/api/src/routes/auth.ts`)
  // checks `isTurnstileEnabled(env)` and rejects a missing token with the
  // same `VALIDATION_FAILED` shape ONLY when the flag is on. Kept as
  // `z.string().min(1)` (not a bare `.optional()`) so a caller who DOES
  // send it still can't send an empty string.
  turnstileToken: z.string().min(1, 'turnstileToken is required').optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const registerResponseSchema = z.object({
  id: z.string(),
  email: emailSchema,
  status: z.literal(UserStatus.PENDING_VERIFICATION),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// ─── POST /v1/auth/verify-email ────────────────────────────────────────────

export const verifyEmailRequestSchema = z.object({
  token: opaqueTokenSchema,
});
export type VerifyEmailRequest = z.infer<typeof verifyEmailRequestSchema>;

// P0-09: verify-email culminates in "issue a session (access JWT + refresh
// cookie), same as login" (spec §6.1) — reuse loginResponseSchema/LoginResponse
// verbatim rather than re-declaring an equivalent-but-drifting shape, so a
// client parses this response exactly like a login response.
export const verifyEmailResponseSchema = loginResponseSchema;
export type VerifyEmailResponse = LoginResponse;

// ─── POST /v1/auth/resend-verification ─────────────────────────────────────
// Rate-limited (spec §10 "signed-URL issuance 60/min per student" pattern —
// this route gets its own bucket in the throttler config, not specced by
// number here). Always returns 202-shaped constant-time ack, mirroring
// forgot-password, so the endpoint can't be used to enumerate accounts.

export const resendVerificationRequestSchema = z.object({
  email: emailSchema,
});
export type ResendVerificationRequest = z.infer<typeof resendVerificationRequestSchema>;

export const genericAckResponseSchema = z.object({
  status: z.literal('ok'),
});
export type GenericAckResponse = z.infer<typeof genericAckResponseSchema>;

// ─── POST /v1/auth/invite/accept ───────────────────────────────────────────
// Token from a teacher/admin-issued invite (spec §2, §6.1) — sets the
// initial password and activates an `INVITED` account.

export const inviteAcceptRequestSchema = z.object({
  token: opaqueTokenSchema,
  name: nameSchema,
  password: passwordSchema,
});
export type InviteAcceptRequest = z.infer<typeof inviteAcceptRequestSchema>;

// P0-09: invite-accept also culminates in "issue a session, same as login" —
// same rationale as verifyEmailResponseSchema above.
export const inviteAcceptResponseSchema = loginResponseSchema;
export type InviteAcceptResponse = LoginResponse;

// ─── POST /v1/auth/password/forgot · /reset ────────────────────────────────
// "rate-limited, constant-time response" (spec §6.1) — `forgot` always
// resolves to `genericAckResponseSchema` regardless of whether the email
// exists, so the response shape can't leak account existence.

export const passwordForgotRequestSchema = z.object({
  email: emailSchema,
});
export type PasswordForgotRequest = z.infer<typeof passwordForgotRequestSchema>;

export const passwordResetRequestSchema = z.object({
  token: opaqueTokenSchema,
  password: passwordSchema,
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

// ─── GET /v1/auth/me ────────────────────────────────────────────────────────

/** Per-level entitlement line, driving the `/app/learn` expiry banner (§9.4)
 * and the teacher/admin roster view. `null` fields cover roles/states with
 * no enrollment concept (ADMIN, TEACHER) or no active enrollment yet. */
export const entitlementLevelSchema = z.object({
  levelId: z.string(),
  levelName: z.string(),
  status: enrollmentStatusEnumSchema,
  expiresAt: z.string().datetime().nullable(),
  daysRemaining: z.number().int().nullable(),
});
export type EntitlementLevel = z.infer<typeof entitlementLevelSchema>;

export const entitlementSummarySchema = z.object({
  levels: z.array(entitlementLevelSchema),
});
export type EntitlementSummary = z.infer<typeof entitlementSummarySchema>;

export const authMeResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: emailSchema,
  role: roleEnumSchema,
  emailVerifiedAt: z.string().datetime().nullable(),
  entitlementSummary: entitlementSummarySchema.nullable(),
  // Direct student create (`POST /v1/students/direct`) forced-rotation flag
  // (`apps/api/migrations/0031_user_must_change_password.sql`): `true` when
  // `User.mustChangePasswordAt` is non-null, so the web app can redirect to
  // a change-password screen after login. Purely advisory — login itself is
  // never blocked on this; the frontend owns the redirect.
  mustChangePassword: z.boolean().default(false),
});
export type AuthMeResponse = z.infer<typeof authMeResponseSchema>;
